import { Alert } from "antd";
import { Users } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useGetActiveGraders, useGradingHeartbeat } from "@hooks";
import { useEffect } from "react";

interface Props {
  examInstanceId: string;
  /** Optional callback reporting how many OTHER assessors are currently grading. */
  onOtherGradersChange?: (count: number) => void;
}

/**
 * Non-blocking grading presence indicator.
 *
 * Several assessors can grade the same exam at the same time. Instead of locking the exam,
 * this shows who is currently grading it and keeps the current assessor's presence alive
 * with a periodic heartbeat.
 */
export const GradingLockIndicator = ({ examInstanceId, onOtherGradersChange }: Props) => {
  const { t } = useTranslation();
  const { data: presence } = useGetActiveGraders(examInstanceId, {
    refetchInterval: 20000,
  });
  const heartbeat = useGradingHeartbeat();

  // Keep this assessor's presence alive while the correction screen is open.
  useEffect(() => {
    if (!examInstanceId) return;
    heartbeat.mutate(examInstanceId);
    const interval = setInterval(() => heartbeat.mutate(examInstanceId), 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [examInstanceId]);

  const others = (presence?.activeGraders ?? []).filter((g) => !g.isCurrentAssessor);

  useEffect(() => {
    onOtherGradersChange?.(others.length);
  }, [others.length, onOtherGradersChange]);

  if (others.length === 0) {
    return null;
  }

  const names = others.map((g) => g.assessorName).join(", ");

  return (
    <Alert
      type="info"
      showIcon={false}
      style={{ marginBottom: 16 }}
      message={
        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Users size={16} />
          <span>{t("grading.currentlyGradingWith", { names })}</span>
        </div>
      }
    />
  );
};

export default GradingLockIndicator;

